import Stepper from "@/components/common/Stepper";
import { formComponents as fc } from "@/components/form";
import { ui } from "@/components/ui";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { SurgeryCliniciansFormFields } from "@/features/events/features/surgeries/components/SurgeryCliniciansFormFields";
import { SurgeryFormFields } from "@/features/events/features/surgeries/components/SurgeryFormFields";
import InjectionDetailsFormFields from "@/features/injection/components/InjectionDetailsFormFields";
import useInjectionOptions from "@/features/injection/hooks/useInjectionOptions";
import InviteForm from "@/features/invite/components/InviteForm";
import ShockwaveFormFields from "@/features/shockwave/components/ShockwaveFormFields";
import { SelectOrganisation } from "@/features/templates/features/select-organisation/SelectOrganisation";
import { services as s } from "@/services";
import {
  templateSettingsSchema,
  type TemplateSettingsSchemaT,
} from "@/types/zod/quickstart-settings";
import { zodResolver } from "@hookform/resolvers/zod";
import { createFileRoute } from "@tanstack/react-router";
import { useNavigate } from "@tanstack/react-router";
import { AlertCircle, Plus, X } from "lucide-react";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useFieldArray } from "react-hook-form";
import type {
  FormViewT,
  OrganisationT,
  ProfileT,
  ScheduleViewT,
  SurgeryOptionT,
} from "../../../types";
import ShockwaveDetailsFormFields from "../../shockwave/components/ShockwaveDetailsFormFields";
import { DiarySummaryAccordion } from "./DiarySummaryAccordion";
export const Route = createFileRoute("/_app/new/diary/invite/diary")({
  component: DiarySummaryForm,
});

export interface StaffT {
  user_id: string;
  name: string;
}
export interface InjectionMethodT {
  injection_method_id: string;
  injection_method: string;
}
export interface InjectionDetailT {
  side?: string | null;
  orthopaedic_area?: string | null;
  site?: string | null;
  subsite?: string | null;
  injection_substance?: string | null;
  dose?: string | null;
  unit?: string | null;
}
export interface ShockwaveDetailT {
  side?: string | null;
  orthopaedic_area?: string | null;
  site?: string | null;
  subsite?: string | null;
  shockwave_type?: string | null;
  impulses?: string | null;
  dose?: string | null;
  unit?: string | null;
}

export function getInjectionPreview(
  {
    staff,
    referringClinicianId,
    injectingClinicianId,
    injectionMethods,
    injectionMethodId,
  }: {
    staff: StaffT[];
    referringClinicianId: string;
    injectingClinicianId: string;
    injectionMethods: InjectionMethodT[];
    injectionMethodId: string;
  },
) {
  const referringClinician =
    staff.find((s) => s.user_id === referringClinicianId)?.name ??
      "Not selected";
  const injectingClinician =
    staff.find((s) => s.user_id === injectingClinicianId)?.name ??
      "Not selected";
  const injectionMethod =
    injectionMethods.find((m) => m.injection_method_id === injectionMethodId)
      ?.injection_method ?? "Not selected";
  return `${referringClinician} • ${injectingClinician} • ${injectionMethod}`;
}

export function getInjectionLocationPreview(details: InjectionDetailT[]) {
  if (!details.length) return null;
  const { side = "", orthopaedic_area = "", site = "", subsite = "" } =
    details[0] || {};
  return [side, orthopaedic_area, site, subsite].filter(Boolean).join(" • ");
}

export function getInjectionSubstancePreview(details: InjectionDetailT[]) {
  if (!details.length) return null;
  const { injection_substance = "", dose = "", unit = "" } = details[0] || {};
  return [injection_substance, dose && unit ? `${dose}${unit}` : dose].filter(
    Boolean,
  ).join(" ");
}

export function getShockwavePreview(
  { staff, referringClinicianId, shockwavingClinicianId }: {
    staff: StaffT[];
    referringClinicianId: string;
    shockwavingClinicianId: string;
  },
) {
  const referring =
    staff.find((s) => s.user_id === referringClinicianId)?.name ??
      "Not selected";
  const therapist =
    staff.find((s) => s.user_id === shockwavingClinicianId)?.name ??
      "Not selected";
  return `${referring} • ${therapist}`;
}

export function getShockwaveLocationPreview(details: ShockwaveDetailT[]) {
  if (!details.length) return null;
  const {
    side = "",
    orthopaedic_area = "",
    site = "",
    subsite = "",
  } = details[0] || {};
  return [side, orthopaedic_area, site, subsite].filter(Boolean).join(
    " • ",
  );
}

export function getShockwaveSubstancePreview(details: ShockwaveDetailT[]) {
  if (!details.length) return null;
  const { shockwave_type = "", impulses = "", dose = "", unit = "" } =
    details[0] || {};
  const base = [
    shockwave_type,
    impulses && `${impulses} impulses`,
    dose && unit ? `${dose}${unit}` : dose,
  ].filter(Boolean).join(" • ");
  return base;
}

export interface DiarySummaryFormProps {
  search: TemplateSettingsSchemaT;
  organisations: OrganisationT[];
  staff: ProfileT[];
  forms: FormViewT[];
  schedules: ScheduleViewT[];
  injectionMethods: InjectionMethodT[];
  surgeryOptions: SurgeryOptionT[];
}

export function DiarySummaryForm({
  search,
  organisations,
  staff,
  forms,
  schedules,
  injectionMethods,
  surgeryOptions,
}: DiarySummaryFormProps) {
  const navigate = useNavigate();

  const form = useForm<TemplateSettingsSchemaT>({
    resolver: zodResolver(templateSettingsSchema),
    defaultValues: {
      ...search,
    },
  });

  // Watch all relevant fields at the top level
  const organisation_id = form.watch("organisation_id");
  const organisation_name = form.watch("organisation_name");
  const trackers = form.watch("trackers") || [];
  const injection_details = form.watch("injection_details") || [];
  const referring_clinician_id = form.watch("referring_clinician_id");
  const injecting_clinician_id = form.watch("injecting_clinician_id");
  const injection_method_id = form.watch("injection_method_id");
  const shockwaving_clinician_id = form.watch("shockwaving_clinician_id");
  const shockwave_details = form.watch("shockwave_details") || [];

  const operating_clinician_id = form.watch(
    "operating_clinician_id",
  );
  const surgery_side = form.watch("surgery_side");
  const surgery_orthopaedic_area = form.watch("surgery_orthopaedic_area");
  const surgery_option_id = form.watch("surgery_option_id");
  const education_name = form.watch("education_name");
  const education_description = form.watch("education_description");
  const educated_by = form.watch("educated_by");
  const contact_id = form.watch("contact_id");

  const {
    fields: trackerFields,
    append: appendTracker,
    remove: removeTracker,
  } = useFieldArray({
    control: form.control,
    name: "trackers",
  });

  // Add this state
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  // Modify the onSubmit function
  const onSubmit = (data: TemplateSettingsSchemaT) => {
    if (hasIncompleteSections) {
      setShowConfirmDialog(true);
    } else {
      navigate({
        to: "/new/diary/invite/user",
        search: data,
      });
    }
  };

  // Add this function
  const handleConfirmProceed = () => {
    navigate({
      to: "/new/diary/invite/user",
      search: form.getValues(),
    });
  };

  useEffect(() => {
    const organizationName = organisations.find((org) =>
      org.organisation_id === organisation_id
    )
      ?.organisation_name ?? "Not selected";

    form.setValue("organisation_name", organizationName);
  }, [organisation_id, organisations, form]);

  // Get tracker preview data
  const getTrackerPreview = (index: number) => {
    if (!trackers[index]) return "Not selected";
    const formId = trackers[index].form_id;
    const scheduleId = trackers[index].schedule_id;
    const formName = forms?.find((f) => f.form_id === formId)?.form_name ??
      "Not selected";
    const scheduleName = schedules?.find((s) => s.schedule_id === scheduleId)
      ?.schedule_name ??
      "Not selected";
    return `${formName} • ${scheduleName}`;
  };

  // Get injection preview data
  const getInjectionPreview = () => {
    const referringClinician =
      staff.find((s) => s.user_id === referring_clinician_id)?.name ??
        "Not selected";
    const injectingClinician =
      staff.find((s) => s.user_id === injecting_clinician_id)?.name ??
        "Not selected";
    const injectionMethod =
      injectionMethods.find((m) =>
        m.injection_method_id === injection_method_id
      )
        ?.injection_method ?? "Not selected";

    return `${referringClinician} • ${injectingClinician} • ${injectionMethod}`;
  };

  // Injection location and substance preview
  const getInjectionLocationPreview = () => {
    if (!injection_details.length) return null;
    const {
      side = "",
      orthopaedic_area = "",
      site = "",
      subsite = "",
    } = injection_details[0] || {};
    return [side, orthopaedic_area, site, subsite].filter(
      Boolean,
    ).join(" • ");
  };
  const getInjectionSubstancePreview = () => {
    if (!injection_details.length) return null;
    const { injection_substance = "", dose = "", unit = "" } =
      injection_details[0] || {};
    return [injection_substance, dose && unit ? `${dose}${unit}` : dose].filter(
      Boolean,
    ).join(" ");
  };

  const hasMissingInjectionDetails = () => {
    return injection_details.some(
      (detail) =>
        !detail.side ||
        !detail.orthopaedic_area ||
        !detail.site ||
        !detail.injection_substance ||
        !detail.dose ||
        !detail.unit,
    );
  };

  const hasMissingShockwaveDetails = () => {
    return shockwave_details.some(
      (detail) =>
        !detail.side ||
        !detail.orthopaedic_area ||
        !detail.site ||
        !detail.shockwave_type ||
        !detail.impulses ||
        !detail.dose ||
        !detail.unit,
    );
  };

  const { methodOptions, clinicianOptions } = useInjectionOptions(
    organisation_id,
  );

  const getSurgeryPreview = () => {
    const referringClinician = staff.find(
      (s) => s.user_id === referring_clinician_id,
    )?.name ?? "Not selected";
    const operatingClinician = staff.find(
      (s) => s.user_id === operating_clinician_id,
    )?.name ?? "Not selected";

    return `${referringClinician} • ${operatingClinician}`;
  };

  const getSurgeryDetailsPreview = () => {
    const parts: string[] = [];
    const side = surgery_side;
    const area = surgery_orthopaedic_area;
    const surgery = surgeryOptions?.find(
      (s) => s.surgery_option_id === surgery_option_id,
    )?.surgery_name;

    if (side) parts.push(side);
    if (area) parts.push(area);
    if (surgery) parts.push(surgery);

    return parts.length > 0 ? parts.join(" • ") : "No details added";
  };

  const hasMissingSurgeryDetails = () => {
    const side = surgery_side;
    const area = surgery_orthopaedic_area;
    const surgery = surgery_option_id;
    return !side || !area || !surgery;
  };

  const getEducationPreview = () => {
    const educatedBy = staff.find((s) => s.user_id === educated_by)?.name ??
      "Not selected";

    return `${educatedBy}`;
  };

  const getEducationDetailsPreview = () => {
    if (!education_name) return "No details added";
    return education_description
      ? `${education_name} • ${education_description}`
      : education_name;
  };

  const hasMissingEducationDetails = () => {
    const name = education_name;
    const description = education_description;
    const educatedBy = educated_by;
    return !name || !description || !educatedBy;
  };

  // Add to getIncompleteSections function
  const getIncompleteSections = () => {
    const sections: string[] = [];
    if (!organisation_id) sections.push("Organisation");
    if (!trackers.length) sections.push("Trackers");
    if (
      search.required_events.includes("injection") &&
      (!referring_clinician_id || !injecting_clinician_id ||
        !injection_method_id)
    ) {
      sections.push("Injection");
    }
    if (
      search.required_events.includes("injection") &&
      hasMissingInjectionDetails()
    ) {
      sections.push("Injection Details");
    }
    if (
      search.required_events.includes("surgery") &&
      (!referring_clinician_id || !operating_clinician_id)
    ) {
      sections.push("Surgery");
    }
    if (
      search.required_events.includes("surgery") &&
      hasMissingSurgeryDetails()
    ) {
      sections.push("Surgery Details");
    }
    if (
      search.required_events.includes("education") &&
      hasMissingEducationDetails()
    ) {
      sections.push("Education");
    }
    if (!contact_id) sections.push("Contact Person");
    if (
      search.required_events.includes("shockwave") &&
      (!referring_clinician_id || !shockwaving_clinician_id)
    ) {
      sections.push("Shockwave");
    }
    if (
      search.required_events.includes("shockwave") &&
      hasMissingShockwaveDetails()
    ) {
      sections.push("Shockwave Details");
    }
    return sections;
  };

  const incompleteSections = getIncompleteSections();
  const hasIncompleteSections = incompleteSections.length > 0;

  // Get shockwave preview data
  const getShockwavePreview = () => {
    const referring =
      staff.find((s) => s.user_id === referring_clinician_id)?.name ??
        "Not selected";
    const therapist =
      staff.find((s) => s.user_id === shockwaving_clinician_id)?.name ??
        "Not selected";
    return `${referring} • ${therapist}`;
  };

  // Helper to determine if a section is complete
  const isSectionComplete = (section: string) => {
    switch (section) {
      case "Organisation":
        return !!organisation_id;
      case "Trackers":
        return trackers.length > 0;
      case "Injection":
        return !!referring_clinician_id && !!injecting_clinician_id &&
          !!injection_method_id;
      case "Injection Details":
        return !hasMissingInjectionDetails();
      case "Surgery":
        return !!referring_clinician_id && !!operating_clinician_id;
      case "Surgery Details":
        return !hasMissingSurgeryDetails();
      case "Education":
        return !hasMissingEducationDetails();
      case "Shockwave":
        return !!referring_clinician_id && !!shockwaving_clinician_id;
      case "Shockwave Details":
        return !hasMissingShockwaveDetails();
      case "Contact Person":
        return !!contact_id;
      default:
        return false;
    }
  };

  return (
    <div className="space-y-8">
      <Stepper
        steps={[
          {
            stage: "current",
            words: "Confirm Diary Details",
            route: { to: "/new/diary/invite/diary" },
          },
          {
            stage: "future",
            words: "Add Patient Information",
            route: { to: "/new/diary/invite/user" },
          },
        ]}
      />

      {hasIncompleteSections && (
        <div className="bg-amber-50 dark:bg-amber-950 p-4 border border-amber-200 dark:border-amber-800 rounded-lg">
          <div className="flex items-start gap-3">
            <AlertCircle className="mt-0.5 w-5 h-5 text-amber-600 dark:text-amber-400" />
            <div className="space-y-1">
              <h3 className="font-medium text-amber-800 dark:text-amber-200">
                Required Information Missing
              </h3>
              <p className="text-amber-700 dark:text-amber-300 text-sm">
                Please complete the following sections before proceeding:
              </p>
              <ul className="text-amber-700 dark:text-amber-300 text-sm list-disc list-inside">
                {incompleteSections.map((section) => (
                  <li key={section}>{section}</li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      )}

      <ui.Form {...form}>
        <form
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-8"
        >
          <ui.Accordion className="space-y-2" type="single" collapsible>
            <DiarySummaryAccordion
              title="Organisation"
              isComplete={isSectionComplete("Organisation")}
              preview={
                <Badge variant="outline" className="ml-2">
                  {organisation_name}
                </Badge>
              }
            >
              <div className="space-y-4">
                <SelectOrganisation
                  organisations={organisations}
                  onValueChange={(value) =>
                    form.setValue(
                      "organisation_id",
                      value,
                    )}
                />
              </div>
            </DiarySummaryAccordion>

            <DiarySummaryAccordion
              title="Trackers"
              isComplete={isSectionComplete("Trackers")}
              preview={
                <Badge variant="outline" className="ml-2">
                  {getTrackerPreview(0)}
                  {trackers.length > 1 && ` +${trackers.length - 1}`}
                </Badge>
              }
            >
              <div className="space-y-4">
                {trackerFields.map((field, index) => {
                  const formId = form.watch(
                    `trackers.${index}.form_id`,
                  ) || "";
                  const scheduleId = form.watch(
                    `trackers.${index}.schedule_id`,
                  ) || "";
                  const formData = (forms ?? []).find((f) =>
                    f.form_id === formId
                  );
                  const scheduleData = (schedules ?? []).find((s) =>
                    s.schedule_id === scheduleId
                  );

                  return (
                    <div
                      key={field.id}
                      className="flex items-start gap-4"
                    >
                      <ui.FormField
                        control={form.control}
                        name={`trackers.${index}.form_id`}
                        render={({ field }) => (
                          <ui.FormItem className="flex-1">
                            <ui.FormLabel>
                              Form
                            </ui.FormLabel>
                            <ui.Select
                              onValueChange={field.onChange}
                              defaultValue={field.value || ""}
                            >
                              <ui.SelectTrigger>
                                <ui.SelectValue placeholder="Select a form" />
                              </ui.SelectTrigger>
                              <ui.SelectContent>
                                {(forms ?? []).map((form) => (
                                  <ui.SelectItem
                                    key={form.form_id ?? ""}
                                    value={form.form_id ?? ""}
                                  >
                                    {form.form_name}
                                  </ui.SelectItem>
                                ))}
                              </ui.SelectContent>
                            </ui.Select>
                            {formData && (
                              <p className="mt-1 text-muted-foreground text-sm">
                                {formData.form_description}
                              </p>
                            )}
                            <ui.FormMessage />
                          </ui.FormItem>
                        )}
                      />
                      <ui.FormField
                        control={form.control}
                        name={`trackers.${index}.schedule_id`}
                        render={({ field }) => (
                          <ui.FormItem className="flex-1">
                            <ui.FormLabel>
                              Schedule
                            </ui.FormLabel>
                            <ui.Select
                              onValueChange={field.onChange}
                              defaultValue={field.value || ""}
                            >
                              <ui.SelectTrigger>
                                <ui.SelectValue placeholder="Select a schedule" />
                              </ui.SelectTrigger>
                              <ui.SelectContent>
                                {(schedules ?? []).map((schedule) => (
                                  <ui.SelectItem
                                    key={schedule.schedule_id ?? ""}
                                    value={schedule.schedule_id ?? ""}
                                  >
                                    {schedule.schedule_name}
                                  </ui.SelectItem>
                                ))}
                              </ui.SelectContent>
                            </ui.Select>
                            {scheduleData && (
                              <p className="mt-1 text-muted-foreground text-sm">
                                {scheduleData.schedule_description}
                              </p>
                            )}
                            <ui.FormMessage />
                          </ui.FormItem>
                        )}
                      />
                      <ui.Button
                        type="button"
                        variant="ghost"
                        size="icon"
                        className="mt-8"
                        onClick={() => removeTracker(index)}
                      >
                        <X className="w-4 h-4" />
                      </ui.Button>
                    </div>
                  );
                })}
                <ui.Button
                  type="button"
                  variant="outline"
                  onClick={() =>
                    appendTracker({
                      form_id: "",
                      schedule_id: "",
                    })}
                  className="w-full"
                >
                  <Plus className="mr-2 w-4 h-4" />
                  Add Tracker
                </ui.Button>
              </div>
            </DiarySummaryAccordion>

            {search.required_events.includes("injection") && (
              <DiarySummaryAccordion
                title="Injection"
                isComplete={isSectionComplete("Injection")}
                preview={
                  <Badge variant="outline" className="ml-2">
                    {getInjectionPreview()}
                  </Badge>
                }
              >
                <div className="space-y-6">
                  <div>
                    {/* Referrer */}
                    <Label htmlFor="referring_clinician_id">
                      Referring Clinician{" "}
                      <span className="text-red-500">
                        *
                      </span>
                    </Label>
                    <fc.ComboBox
                      autoFocus
                      name="referring_clinician_id"
                      description="The clinician who requests the injection"
                      options={clinicianOptions}
                      clearable={true}
                      addOption={<InviteForm />}
                    />
                  </div>
                  <div>
                    {/* Injector */}
                    <Label htmlFor="injecting_clinician_id">
                      Injecting Clinician
                    </Label>
                    <fc.ComboBox
                      name="injecting_clinician_id"
                      description="The clinician who performs the injection"
                      options={clinicianOptions}
                      clearable={true}
                      addOption={<InviteForm />}
                    />
                  </div>
                  <div>
                    {/* Method */}
                    <Label htmlFor="injection_method_id">
                      Injection Method
                    </Label>
                    <fc.ComboBox
                      name="injection_method_id"
                      description="How and where injection is performed"
                      options={methodOptions}
                      clearable={true}
                    />
                  </div>
                </div>
              </DiarySummaryAccordion>
            )}

            {search.required_events.includes("injection") && (
              <DiarySummaryAccordion
                title="Injection Details"
                isComplete={isSectionComplete("Injection Details")}
                preview={
                  <Badge variant="outline" className="ml-2">
                    {getInjectionLocationPreview()} •{" "}
                    {getInjectionSubstancePreview()}
                  </Badge>
                }
              >
                <InjectionDetailsFormFields
                  injectionId={search.injection_id ??
                    s.uuidv4()}
                />
              </DiarySummaryAccordion>
            )}

            {search.required_events.includes("surgery") && (
              <DiarySummaryAccordion
                title="Surgery"
                isComplete={isSectionComplete("Surgery")}
                preview={
                  <Badge variant="outline" className="ml-2">
                    {getSurgeryPreview()}
                  </Badge>
                }
              >
                <SurgeryCliniciansFormFields
                  organisationId={organisation_id}
                />
              </DiarySummaryAccordion>
            )}

            {search.required_events.includes("surgery") && (
              <DiarySummaryAccordion
                title="Surgery Details"
                isComplete={isSectionComplete("Surgery Details")}
                preview={
                  <Badge variant="outline" className="ml-2">
                    {getSurgeryDetailsPreview()}
                  </Badge>
                }
              >
                <SurgeryFormFields
                  sideFieldName="surgery_side"
                  orthopaedicAreaFieldName="surgery_orthopaedic_area"
                  surgeryOptionIdFieldName="surgery_option_id"
                />
              </DiarySummaryAccordion>
            )}

            {search.required_events.includes("education") && (
              <DiarySummaryAccordion
                title="Education"
                isComplete={isSectionComplete("Education")}
                preview={
                  <Badge variant="outline" className="ml-2">
                    {getEducationPreview()}
                  </Badge>
                }
              >
                <div className="space-y-6">
                  <div>
                    <Label htmlFor="education_name">
                      Education Name{" "}
                      <span className="text-red-500">
                        *
                      </span>
                    </Label>
                    <fc.Input
                      name="education_name"
                      placeholder="e.g., Pain Management Workshop, Post-Surgery Care Instructions"
                    />
                  </div>

                  <div>
                    <Label htmlFor="education_description">
                      Description{" "}
                      <span className="text-red-500">
                        *
                      </span>
                    </Label>
                    <fc.Textarea
                      name="education_description"
                      placeholder="Describe what was discussed or taught during this education session"
                      className="min-h-[100px]"
                    />
                  </div>

                  <div>
                    <Label htmlFor="educated_by">
                      Educated By{" "}
                      <span className="text-red-500">
                        *
                      </span>
                    </Label>
                    <fc.ComboBox
                      name="educated_by"
                      description="The clinician who provided the education"
                      options={clinicianOptions}
                      clearable={true}
                      addOption={<InviteForm />}
                    />
                  </div>
                </div>
              </DiarySummaryAccordion>
            )}

            <DiarySummaryAccordion
              title="Contact Person"
              isComplete={isSectionComplete("Contact Person")}
              preview={
                <Badge variant="outline" className="ml-2">
                  {staff.find((s) => s.user_id === contact_id)?.name}
                </Badge>
              }
            >
              <div className="space-y-4">
                <ui.FormField
                  control={form.control}
                  name="contact_id"
                  render={({ field }) => (
                    <ui.FormItem>
                      <ui.FormLabel>
                        Contact Person
                      </ui.FormLabel>
                      <ui.Select
                        onValueChange={field
                          .onChange}
                        defaultValue={field.value ||
                          ""}
                      >
                        <ui.SelectTrigger>
                          <ui.SelectValue placeholder="Select a contact" />
                        </ui.SelectTrigger>
                        <ui.SelectContent>
                          {staff.map((person) => (
                            <ui.SelectItem
                              key={person
                                .user_id}
                              value={person
                                .user_id ||
                                ""}
                            >
                              {person.name}
                            </ui.SelectItem>
                          ))}
                        </ui.SelectContent>
                      </ui.Select>
                      <ui.FormDescription>
                        Someone the patient can contact if they want further
                        contact with the clinic
                      </ui.FormDescription>
                      <ui.FormMessage />
                    </ui.FormItem>
                  )}
                />
              </div>
            </DiarySummaryAccordion>

            {search.required_events.includes("shockwave") && (
              <DiarySummaryAccordion
                title="Shockwave"
                isComplete={isSectionComplete("Shockwave")}
                preview={
                  <Badge variant="outline" className="ml-2">
                    {getShockwavePreview()}
                  </Badge>
                }
              >
                <ShockwaveFormFields organisationId={organisation_id} />
              </DiarySummaryAccordion>
            )}

            {search.required_events.includes("shockwave") && (
              <DiarySummaryAccordion
                title="Shockwave Details"
                isComplete={isSectionComplete("Shockwave Details")}
                preview={
                  <Badge variant="outline" className="ml-2">
                    {getShockwaveLocationPreview(shockwave_details)} •{" "}
                    {getShockwaveSubstancePreview(shockwave_details)}
                  </Badge>
                }
              >
                <ShockwaveDetailsFormFields
                  shockwaveId={search.shockwave_id ?? s.uuidv4()}
                />
              </DiarySummaryAccordion>
            )}
          </ui.Accordion>

          <div className="flex justify-end gap-4">
            <Dialog
              open={showConfirmDialog}
              onOpenChange={setShowConfirmDialog}
            >
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>
                    Incomplete Information
                  </DialogTitle>
                  <DialogDescription>
                    The following sections are incomplete:
                  </DialogDescription>
                </DialogHeader>
                <div className="py-4">
                  <ul className="text-muted-foreground text-sm list-disc list-inside">
                    {incompleteSections.map((section) => (
                      <li key={section}>{section}</li>
                    ))}
                  </ul>
                </div>
                <DialogFooter>
                  <ui.Button
                    variant="outline"
                    onClick={() => setShowConfirmDialog(false)}
                  >
                    Go Back
                  </ui.Button>
                  <ui.Button
                    onClick={handleConfirmProceed}
                    className="bg-amber-600 hover:bg-amber-700"
                  >
                    Proceed Anyway
                  </ui.Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>

            <ui.Button
              onClick={() => console.log(form.formState.errors)}
              type="submit"
              variant={hasIncompleteSections ? "secondary" : "default"}
            >
              {hasIncompleteSections
                ? `Continue (${incompleteSections.length} section${
                  incompleteSections.length === 1 ? "" : "s"
                } incomplete)`
                : "Next"}
            </ui.Button>
          </div>
        </form>
      </ui.Form>
    </div>
  );
}
export default DiarySummaryForm;
